import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ArrowRight, Loader2, User, BookOpen, Mail, Lock, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('auth');
  const isRTL = i18n.language === 'ar';
  
  const [view, setView] = useState('login'); // 'login' | 'signup' | 'verify' | 'forgot'
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    otp: '',
    role: 'student'
  });

  const from = location.state?.from?.pathname || "/courses";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleRoleChange = (role) => {
    setFormData({ ...formData, role });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
        console.log("Attempting signup with:", { name: formData.name, email: formData.email, role: formData.role });
        console.log("Backend URL:", BACKEND_URL);
        
        const res = await axios.post(`${BACKEND_URL}/api/supabase-auth/register`, {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            role: formData.role
        });
        
        console.log("Signup response:", res.data);
        toast.success("Account created! Please check your email to verify your account.");
        // Show email sent message
        setView('verify');
    } catch (error) {
        console.error("Signup error:", error);
        console.error("Error response:", error.response);
        console.error("Error message:", error.message);
        
        // More specific error messages
        if (error.response?.data?.detail) {
            toast.error(error.response.data.detail);
        } else if (error.message === "Network Error") {
            toast.error("Network error. Please check your internet connection.");
        } else {
            toast.error("Signup failed. Please try again.");
        }
    } finally {
        setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
        await axios.post(`${BACKEND_URL}/api/supabase-auth/resend-verification`, {
            email: formData.email
        });
        toast.success("Verification email resent! Please check your inbox.");
    } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to resend verification email.");
    } finally {
        setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/supabase-auth/forgot-password`, {
        email: formData.email
      });
      toast.success("Password reset email sent! Please check your inbox.");
      setView('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
        console.log("Attempting login with:", formData.email);
        console.log("Backend URL:", BACKEND_URL);
        
        const res = await axios.post(`${BACKEND_URL}/api/supabase-auth/login`, {
            email: formData.email,
            password: formData.password
        });

        console.log("Login response:", res.data);
        
        const { access_token, user_name, role } = res.data;
        
        localStorage.setItem('faheem_auth', 'true');
        localStorage.setItem('faheem_token', access_token);
        localStorage.setItem('faheem_user', user_name);
        localStorage.setItem('faheem_role', role);

        // Dispatch custom event to notify other components about auth change
        window.dispatchEvent(new Event('authChange'));

        toast.success(`Welcome back, ${user_name}!`);
        
        // Check if user is a teacher and redirect accordingly
        try {
            const teacherCheck = await axios.get(`${BACKEND_URL}/api/teacher/check`, {
                params: { access_token }
            });
            if (teacherCheck.data?.is_teacher) {
                navigate('/teacher-profile', { replace: true });
                return;
            }
        } catch (err) {
            // Not a teacher, continue to default route
        }
        
        navigate(from, { replace: true });
    } catch (error) {
        console.error("Login error:", error);
        console.error("Error response:", error.response);
        console.error("Error message:", error.message);
        
        // More specific error messages
        if (error.response?.data?.detail) {
            toast.error(error.response.data.detail);
        } else if (error.message === "Network Error") {
            toast.error("Network error. Please check your internet connection.");
        } else {
            toast.error("Login failed. Check your credentials.");
        }
    } finally {
        setIsLoading(false);
    }
  };

  // Get title and description based on view
  const getHeaderContent = () => {
    switch(view) {
      case 'login':
        return { title: t('enjoyJourney'), desc: t('pleaseEnterCredentials') };
      case 'signup':
        return { title: t('createAccount'), desc: t('joinCommunity') };
      case 'verify':
        return { title: t('verifyEmail'), desc: t('enterCodeSent') };
      default:
        return { title: t('enjoyJourney'), desc: t('pleaseEnterCredentials') };
    }
  };

  const headerContent = getHeaderContent();
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="h-screen w-full grid lg:grid-cols-2 overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Left Side - Form Area with Gray Background */}
      <div className="relative flex flex-col justify-center p-8 md:p-12 lg:p-16 h-full overflow-y-auto">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_f8ffea51-d543-4d89-8a26-4f74f0b6ea0e/artifacts/gtac4d5y_graybackground.jpg)' }}
        >
          <div className="absolute inset-0 bg-white/90"></div>
        </div>

        <Link 
          to="/" 
          className={`absolute top-8 z-10 ${isRTL ? 'left-8 md:left-12 lg:left-16' : 'right-8 md:right-12 lg:right-16'} inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors`}
        >
            <BackArrow className="w-4 h-4" /> {t('backToHome')}
        </Link>

        <div className={`relative z-10 max-w-md w-full mx-auto space-y-8 mt-12 lg:mt-0`}>
            <div className={`${isRTL ? 'text-right' : 'text-center lg:text-left'} space-y-2`}>
                <Link to="/" className={`inline-flex lg:hidden items-center gap-2 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <img 
                        src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                        alt="FaheemGPT Logo" 
                        className="w-10 h-10 rounded-lg object-cover" 
                    />
                    <span className="text-xl font-bold text-slate-900">FaheemGPT</span>
                </Link>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                    {headerContent.title}
                </h1>
                <p className="text-slate-500 text-lg">
                    {headerContent.desc}
                </p>
            </div>

            {view === 'login' && (
                <div className="space-y-6 animate-in fade-in-50 slide-in-from-left-2 duration-300">
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className={`text-slate-700 ${isRTL ? 'block text-right' : ''}`}>{t('emailAddress')}</Label>
                            <div className="relative">
                                <Mail 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="email" 
                                  value={formData.email} 
                                  onChange={handleChange} 
                                  placeholder={t('emailPlaceholder')} 
                                  required 
                                  type="email" 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'left' } : { paddingLeft: '2.5rem' }}
                                  dir="ltr"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Label htmlFor="password" className="text-slate-700">{t('password')}</Label>
                                <button type="button" onClick={() => setView('forgot')} className="p-0 h-auto text-xs text-blue-600 font-medium hover:underline">{t('forgotPassword')}</button>
                            </div>
                            <div className="relative">
                                <Lock 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="password" 
                                  value={formData.password} 
                                  onChange={handleChange} 
                                  required 
                                  type="password" 
                                  placeholder={t('passwordPlaceholder')} 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'left' } : { paddingLeft: '2.5rem' }}
                                  dir="ltr"
                                />
                            </div>
                        </div>
                        <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11" type="submit" disabled={isLoading}>
                            {isLoading ? <><Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} /> {t('signingIn')}</> : t('login')}
                        </Button>
                    </form>
                    <p className={`text-center text-sm text-slate-500 mt-6`}>
                        {t('noAccount')}{" "}
                        <button onClick={() => setView('signup')} className="text-slate-900 font-semibold hover:underline underline-offset-4">
                            {t('signup')}
                        </button>
                    </p>
                </div>
            )}

            {view === 'forgot' && (
                <div className="space-y-6 animate-in fade-in-50 slide-in-from-right-2 duration-300">
                    <div className={`text-center mb-6`}>
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <KeyRound className="w-8 h-8 text-blue-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900">{t('forgotPasswordTitle')}</h2>
                        <p className="text-sm text-slate-500 mt-2">{t('forgotPasswordDesc')}</p>
                    </div>
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className={`text-slate-700 ${isRTL ? 'block text-right' : ''}`}>{t('emailAddress')}</Label>
                            <div className="relative">
                                <Mail 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="email" 
                                  value={formData.email} 
                                  onChange={handleChange} 
                                  placeholder={t('emailPlaceholder')} 
                                  required 
                                  type="email" 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'left' } : { paddingLeft: '2.5rem' }}
                                  dir="ltr"
                                />
                            </div>
                        </div>
                        <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11" type="submit" disabled={isLoading}>
                            {isLoading ? <><Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} /> {t('sending')}</> : t('sendResetLink')}
                        </Button>
                    </form>
                    <p className={`text-center text-sm text-slate-500 mt-6`}>
                        {t('rememberPassword')}{" "}
                        <button onClick={() => setView('login')} className="text-slate-900 font-semibold hover:underline underline-offset-4">
                            {t('backToLogin')}
                        </button>
                    </p>
                </div>
            )}

            {view === 'signup' && (
                <div className="space-y-6 animate-in fade-in-50 slide-in-from-right-2 duration-300">
                    <Tabs defaultValue="student" onValueChange={handleRoleChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-slate-100 rounded-xl border border-slate-200 h-auto">
                            <TabsTrigger 
                                value="student" 
                                className={`flex gap-2 py-3 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}
                                data-[state=active]:bg-slate-200 
                                data-[state=active]:text-blue-600
                                data-[state=active]:shadow-sm`}
                            >
                                <User className="w-4 h-4" /> {t('student')}
                            </TabsTrigger>

                            <TabsTrigger 
                                value="teacher" 
                                className={`flex gap-2 py-3 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}
                                data-[state=active]:bg-slate-200
                                data-[state=active]:text-purple-600
                                data-[state=active]:shadow-sm`}
                            >
                                <BookOpen className="w-4 h-4" /> {t('teacher')}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <form onSubmit={handleSignup} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name" className={`text-slate-700 ${isRTL ? 'block text-right' : ''}`}>{t('fullName')}</Label>
                            <div className="relative">
                                <User 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="name" 
                                  value={formData.name} 
                                  onChange={handleChange} 
                                  placeholder={t('namePlaceholder')} 
                                  required 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'right' } : { paddingLeft: '2.5rem' }}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className={`text-slate-700 ${isRTL ? 'block text-right' : ''}`}>{t('emailAddress')}</Label>
                            <div className="relative">
                                <Mail 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="email" 
                                  value={formData.email} 
                                  onChange={handleChange} 
                                  placeholder={t('emailPlaceholder')} 
                                  required 
                                  type="email" 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'left' } : { paddingLeft: '2.5rem' }}
                                  dir="ltr"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className={`text-slate-700 ${isRTL ? 'block text-right' : ''}`}>{t('password')}</Label>
                            <div className="relative">
                                <Lock 
                                  className="absolute top-3 h-4 w-4 text-slate-400" 
                                  style={isRTL ? { right: '0.75rem', left: 'auto' } : { left: '0.75rem', right: 'auto' }}
                                />
                                <Input 
                                  id="password" 
                                  value={formData.password} 
                                  onChange={handleChange} 
                                  required 
                                  type="password" 
                                  placeholder={t('passwordPlaceholder')} 
                                  className="bg-slate-50"
                                  style={isRTL ? { paddingRight: '2.5rem', paddingLeft: '0.75rem', textAlign: 'left' } : { paddingLeft: '2.5rem' }}
                                  dir="ltr"
                                />
                            </div>
                        </div>
                        <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11" type="submit" disabled={isLoading}>
                            {isLoading ? <><Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} /> {t('creatingAccount')}</> : t('createAccount')}
                        </Button>
                    </form>
                    <p className={`text-center text-sm text-slate-500 mt-6`}>
                        {t('haveAccount')}{" "}
                        <button onClick={() => setView('login')} className="text-slate-900 font-semibold hover:underline underline-offset-4">
                            {t('login')}
                        </button>
                    </p>
                </div>
            )}

            {view === 'verify' && (
                <div className="space-y-6 animate-in fade-in-50 slide-in-from-right-2 duration-300">
                    <div className={`text-center space-y-4`}>
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <Mail className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{t('checkYourEmail')}</h3>
                            <p className="text-sm text-slate-600">
                                {t('verificationLinkSent')} <br/>
                                <strong className="text-slate-900" dir="ltr">{formData.email}</strong>
                            </p>
                        </div>
                    </div>
                    
                    <div className={`p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 ${isRTL ? 'text-right' : ''}`}>
                        <strong>{t('nextSteps')}</strong>
                        <ol className={`list-decimal mt-2 space-y-1 ${isRTL ? 'mr-4' : 'ml-4'}`}>
                            <li>{t('step1')}</li>
                            <li>{t('step2')}</li>
                            <li>{t('step3')}</li>
                        </ol>
                    </div>

                    <div className="space-y-3">
                        <Button 
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11" 
                            onClick={() => setView('login')}
                        >
                            {t('goToSignIn')}
                        </Button>
                        
                        <Button 
                            variant="outline" 
                            className="w-full h-11" 
                            onClick={handleResendVerification}
                            disabled={isLoading}
                        >
                            {isLoading ? <><Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} /> {t('resending')}</> : t('resendVerificationEmail')}
                        </Button>
                    </div>
                    
                    <p className={`text-center text-xs text-slate-500`}>
                        {t('checkSpam')}
                    </p>
                </div>
            )}

        </div>
      </div>

      {/* Right Side - Visual & Branding (Hidden on Mobile) */}
      <div className={`hidden lg:flex relative flex-col justify-between p-12 lg:p-16 text-white overflow-hidden ${isRTL ? 'text-right' : ''}`}>
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)' }}
        >
          <div className="absolute inset-0 bg-slate-900/90"></div>
        </div>
        
        {/* Decorative Gradients */}
        <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-3xl -translate-y-1/2 ${isRTL ? '-translate-x-1/2' : 'translate-x-1/2'}`} />
        <div className={`absolute bottom-0 ${isRTL ? 'right-0' : 'left-0'} w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-3xl translate-y-1/2 ${isRTL ? 'translate-x-1/2' : '-translate-x-1/2'}`} />
        
        <div className="relative z-10">
            <Link to="/" className={`inline-flex items-center gap-3 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <img 
                    src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                    alt="FaheemGPT Logo" 
                    className="w-10 h-10 rounded-lg object-cover border-2 border-white/10" 
                />
                <span className="text-xl font-bold tracking-tight">FaheemGPT</span>
            </Link>
        </div>

        <div className={`relative z-10 max-w-lg space-y-8 ${isRTL ? 'text-right ml-auto' : ''}`}>
            <h2 className="text-4xl font-bold leading-tight">
                {t('experienceFuture')}
            </h2>
            
            <div className="space-y-6 pt-8">
                <div className={`flex items-start gap-4 group ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                        <h3 className="font-semibold text-white text-lg">{t('feature1Title')}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{t('feature1Desc')}</p>
                    </div>
                </div>
                <div className={`flex items-start gap-4 group ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                        <h3 className="font-semibold text-white text-lg">{t('feature2Title')}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{t('feature2Desc')}</p>
                    </div>
                </div>
                <div className={`flex items-start gap-4 group ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-green-400" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                        <h3 className="font-semibold text-white text-lg">{t('feature3Title')}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{t('feature3Desc')}</p>
                    </div>
                </div>
            </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-sm text-slate-400 mt-auto">
            {null}
        </div>
      </div>

    </div>
  );
}
